/**
 * Composes the full property detail page (in-dialog body).
 *
 * Left column: DetailHero + DealInputs.
 * Right column: tabs (Checklist · BRRRR · Flip · Rental · Cashflow · Rehab · Overview).
 *
 * Owns the central DealInputsState tree. Reads persisted values from
 * `useDealSettings(pipelinePropertyId)`. Persistence is **manual**: the
 * parent (PropertyDetailPage) drives the "Save changes" button via the
 * imperative handle exposed by forwardRef, and listens for `isDirty` /
 * `isSaving` updates through the `onSaveStateChange` callback.
 *
 * When `pipelinePropertyId` is null (property not yet saved) settings are
 * session-only: edits live in component state but cannot be persisted.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Target } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useDealSettings } from "@/hooks/use-deal-settings";
import type { Strategy } from "@/types/pipeline";

import { DealInputs } from "./DealInputs";
import { DetailHero } from "./DetailHero";
import {
  buildInitialInputs,
  buildRehabDiff,
  deriveMetrics,
  isRehabDiffEmpty,
  mergeWithPersisted,
  parseSqft,
  toPersistedPatch,
  type DealInputsState,
  type PropertySnapshot,
} from "./detail-helpers";
import { BrrrrSnapshot } from "./panels/BrrrrSnapshot";
import { ChecklistPanel } from "./panels/ChecklistPanel";
import { FlipAnalysis } from "./panels/FlipAnalysis";
import { MonthlyCashflow } from "./panels/MonthlyCashflow";
import { OverviewPanel } from "./panels/OverviewPanel";
import { RehabBreakdown } from "./panels/RehabBreakdown";
import { RentAnalysis } from "./panels/RentAnalysis";

/**
 * Underline-on-active tab style. Overrides shadcn's default pill style to
 * match the design's `.tab` rule: flat trigger with a 2px primary border on
 * the active row. Applied to every TabsTrigger inside the detail content
 * so we don't have to swap the underlying Tabs primitive.
 */
const tabTriggerStyle = cn(
  "relative rounded-none border-b-2 border-transparent bg-transparent px-4 py-3",
  "text-[13.5px] font-semibold text-muted-foreground shadow-none",
  "hover:text-foreground",
  "data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none",
  "disabled:cursor-not-allowed disabled:opacity-45",
);

/** Parent-facing handle for triggering a save imperatively. */
export interface PropertyDetailContentHandle {
  save: () => void;
  /**
   * Returns the patch the next save would send to property_setting.
   * Used by the "Add to Pipeline" flow so the user's current deal inputs
   * can be persisted alongside the pipeline_properties insert.
   */
  getSavePayload: () => Record<string, unknown>;
}

/** State the parent header reads to drive its "Save changes" button. */
export interface PropertyDetailSaveState {
  /** True when the user has edited inputs since the last save (or initial load). */
  isDirty: boolean;
  /** True while the saveSettings mutation is in-flight. */
  isSaving: boolean;
  /** True when persistence is wired (deal has a pipeline_properties row). */
  canSave: boolean;
}

interface PropertyDetailContentProps {
  snapshot: PropertySnapshot;
  /** When set, the deal is in the pipeline — Checklist tab + settings persistence are wired. */
  pipelinePropertyId: string | null;
  /** Current pipeline stage id (needed for ChecklistPanel). */
  pipelineId?: string | null;
  /** Current pipeline stage title (for display in checklist header). */
  pipelineTitle?: string | null;
  /** Initial strategy (defaults to BRRRR). The user can change via the Financing strategy bar. */
  initialStrategy?: Strategy;
  /**
   * Controlled strategy. When provided together with `onStrategyChange`,
   * the parent owns the strategy state (e.g. so the Pipeline Strategy Bar
   * above the hero stays in sync with the Financing-panel strategy bar).
   */
  strategy?: Strategy;
  /** Notified when the in-panel strategy bar changes the strategy. */
  onStrategyChange?: (strategy: Strategy) => void;
  /**
   * Persist the strategy. Called from `save()` only when the strategy
   * diverges from its last-saved baseline. Resolves true on success so the
   * caller can advance its dirty baseline.
   */
  onStrategySave?: (strategy: Strategy) => Promise<boolean>;
  /**
   * The committed strategy from `pipeline_properties.strategy`. When set,
   * the right-rail tabs hide the non-active strategy triggers (the deal has
   * been committed to one strategy). When null, all three are visible with
   * the inactive ones dimmed-disabled — used on the Find detail view before
   * the property is promoted to a pipeline.
   */
  pipelineStrategy?: Strategy | null;
  /**
   * Hide the Checklist tab even when the deal has a pipeline stage. Set when the
   * detail is opened from the Properties section — owned properties have no
   * actionable checklist, so the right rail defaults to the strategy tab
   * (mirrors the design's `fromProperties` checklist-tab removal).
   */
  hideChecklist?: boolean;
  /**
   * Opened from the Properties (owned) section. Adjusts the finance section:
   * hides the acquisition-only MAO panel, keeps Financing expanded, and shows
   * the per-loan Loan Date + Maturity tracker (mirrors the design's
   * `fromProperties` branch).
   */
  fromProperties?: boolean;
  /** Bubbles dirty + saving state up so the page header can render the Save button. */
  onSaveStateChange?: (state: PropertyDetailSaveState) => void;
}

export const PropertyDetailContent = forwardRef<
  PropertyDetailContentHandle,
  PropertyDetailContentProps
>(function PropertyDetailContent(
  {
    snapshot,
    pipelinePropertyId,
    pipelineId,
    pipelineTitle,
    initialStrategy,
    strategy: controlledStrategy,
    onStrategyChange,
    onStrategySave,
    pipelineStrategy,
    hideChecklist,
    fromProperties,
    onSaveStateChange,
  },
  ref,
) {
  // Settings persistence — fetches existing row + rehab line item rows +
  // provides the save mutation. `rehabRows` is the source of truth for the
  // diff we send to `saveSettings` on the next click of "Save changes".
  const {
    row: persistedRow,
    rehabRows,
    saveSettings,
    isLoading,
  } = useDealSettings(pipelinePropertyId);

  // Initial state derived from the snapshot, then merged with persisted settings.
  const initial = useMemo(() => buildInitialInputs(snapshot), [snapshot]);
  const [state, setState] = useState<DealInputsState>(initial);
  // Strategy is controlled when both `strategy` + `onStrategyChange` props
  // are provided; otherwise we manage it locally. The setter notifies the
  // parent (when controlled) AND keeps the local fallback in sync so the
  // component renders correctly even mid-transition.
  const [internalStrategy, setInternalStrategy] = useState<Strategy>(
    controlledStrategy ?? initialStrategy ?? "BRRRR",
  );
  const strategy = controlledStrategy ?? internalStrategy;
  const setStrategy = useCallback(
    (s: Strategy) => {
      setInternalStrategy(s);
      onStrategyChange?.(s);
    },
    [onStrategyChange],
  );

  // Snapshot of "last persisted" state, used for dirty tracking. JSON-comparing
  // the full DealInputsState tree against this is a cheap reliable signal —
  // the state is small (~1KB serialised) and only diffed when isDirty is read.
  const lastSavedRef = useRef<string>(JSON.stringify(initial));
  const lastSavedStrategyRef = useRef<Strategy>(initialStrategy ?? "BRRRR");

  /** Strategy-tab mapping — Rental tab is keyed "rent" to match the mockup. */
  function strategyTabKey(s: Strategy): "brrrr" | "flip" | "rent" {
    if (s === "Flip") return "flip";
    if (s === "Rental") return "rent";
    return "brrrr";
  }
  // Checklist tab only applies to deals being worked in a pipeline stage, and
  // is suppressed entirely when opened from the Properties section.
  const showChecklist = Boolean(
    pipelinePropertyId && pipelineId && !hideChecklist,
  );
  const [activeTab, setActiveTab] = useState<string>(
    showChecklist ? "checklist" : strategyTabKey(initialStrategy ?? "BRRRR"),
  );

  // When the strategy changes, follow it on the right-hand tabs (matches
  // applyStrategy() in find-properties.html). If the user is on a non-
  // strategy tab (rehab/cashflow/overview/checklist), leave it alone.
  useEffect(() => {
    // Monthly Cash Flow isn't meaningful for a Flip — if the user is viewing it
    // when Flip becomes active, bounce them to the Flip strategy tab (matches
    // hideNonMatchingStrategyTabs() in find-properties.html).
    if (strategy === "Flip" && activeTab === "cashflow") {
      setActiveTab("flip");
      return;
    }
    const strategyKeys = ["brrrr", "flip", "rent"];
    if (!strategyKeys.includes(activeTab)) return;
    const target = strategyTabKey(strategy);
    if (activeTab !== target) setActiveTab(target);
    // strategyTabKey is stable; we only want to fire on strategy change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy]);

  // ── Manual save (replaces the old 600ms auto-save debounce) ──
  // Track strategy and deal-inputs dirtiness separately so the save dispatch
  // can hit only the endpoints whose data actually changed.
  const stateJson = JSON.stringify(state);
  const isInputsDirty = stateJson !== lastSavedRef.current;
  const isStrategyDirty = strategy !== lastSavedStrategyRef.current;
  const isDirty = isInputsDirty || isStrategyDirty;

  // When the persisted row OR the rehab line-item rows change, merge them
  // onto our state. The fingerprint covers both — after a save the rehab
  // rows refetch with fresh ids that need to flow back into state items
  // (newly inserted items had no id pre-save).
  //
  // We skip the merge once it has already run AND state diverges from the
  // last-saved baseline (`isInputsDirty`), so an in-flight edit isn't
  // clobbered by a background refetch. Trade-off: edits made between save
  // and the refetch don't get their ids backfilled, and the next save will
  // treat them as fresh inserts.
  const mergedFingerprintRef = useRef<string | null>(null);
  const persistedFingerprint = persistedRow
    ? `${persistedRow.id}:${persistedRow.updated_at ?? ""}:${rehabRows
        .map((r) => `${r.id}|${r.estimated_cost}|${r.label}`)
        .join(",")}`
    : null;
  useEffect(() => {
    if (!persistedRow || persistedFingerprint === null) return;
    if (mergedFingerprintRef.current === persistedFingerprint) return;
    const isFirstMerge = mergedFingerprintRef.current === null;
    if (!isFirstMerge && isInputsDirty) return;
    setState((prev) => {
      const merged = mergeWithPersisted(prev, persistedRow, rehabRows, strategy);
      lastSavedRef.current = JSON.stringify(merged);
      return merged;
    });
    mergedFingerprintRef.current = persistedFingerprint;
    // isInputsDirty intentionally not in deps — it's only a guard against
    // clobbering edits, not a re-merge trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedRow, rehabRows, persistedFingerprint]);

  // Local pending flag for the strategy-side mutation (parent-owned).
  const [isStrategySaving, setIsStrategySaving] = useState(false);

  const save = useCallback(() => {
    if (!pipelinePropertyId) return;
    if (!isDirty) return;

    if (isInputsDirty) {
      const settingsPatch = toPersistedPatch(state, strategy);
      const rehabDiff = buildRehabDiff(
        rehabRows,
        state.rehabLineItems ?? [],
        state.rehabCustomItems ?? [],
      );
      const payload = isRehabDiffEmpty(rehabDiff)
        ? settingsPatch
        : { ...settingsPatch, rehab: rehabDiff };
      const snapshotJson = stateJson;
      saveSettings.mutate(payload, {
        onSuccess: () => {
          lastSavedRef.current = snapshotJson;
        },
      });
    }

    if (isStrategyDirty && onStrategySave) {
      const snapshotStrategy = strategy;
      setIsStrategySaving(true);
      void onStrategySave(snapshotStrategy)
        .then((ok) => {
          if (ok) lastSavedStrategyRef.current = snapshotStrategy;
        })
        .finally(() => setIsStrategySaving(false));
    }
  }, [
    pipelinePropertyId,
    isDirty,
    isInputsDirty,
    isStrategyDirty,
    state,
    stateJson,
    strategy,
    rehabRows,
    saveSettings,
    onStrategySave,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      save,
      // For the "Add to Pipeline" flow: include the same diff payload so
      // newly-built rehab scope items are persisted at promotion time.
      getSavePayload: () => {
        const settingsPatch = toPersistedPatch(state, strategy);
        const rehabDiff = buildRehabDiff(
          rehabRows,
          state.rehabLineItems ?? [],
          state.rehabCustomItems ?? [],
        );
        return isRehabDiffEmpty(rehabDiff)
          ? settingsPatch
          : { ...settingsPatch, rehab: rehabDiff };
      },
    }),
    [save, state, strategy, rehabRows],
  );

  // Notify the parent header whenever the save-state changes so it can
  // enable/disable + relabel the "Save changes" button.
  useEffect(() => {
    onSaveStateChange?.({
      isDirty,
      isSaving: saveSettings.isPending || isStrategySaving,
      canSave: Boolean(pipelinePropertyId),
    });
  }, [
    isDirty,
    saveSettings.isPending,
    isStrategySaving,
    pipelinePropertyId,
    onSaveStateChange,
  ]);

  const sqft = parseSqft(snapshot.sqft);
  const metrics = useMemo(
    () =>
      deriveMetrics({
        state,
        strategy,
        sqft,
        financingEnabled: state.financingEnabled,
      }),
    [state, strategy, sqft],
  );

  function patch(p: Partial<DealInputsState>) {
    setState((prev) => ({ ...prev, ...p }));
  }

  // Strategy tab visibility rule:
  //   - When `pipelineStrategy` is set (the deal has been promoted with a
  //     committed strategy), HIDE the other two strategy tabs entirely —
  //     the user is locked in.
  //   - When `pipelineStrategy` is null (Find detail before promotion), show
  //     all three with the inactive ones disabled + dimmed so the user can
  //     see what other strategies look like before committing.
  const hasCommittedStrategy = pipelineStrategy != null;
  const tabValue = activeTab;
  const isPipelined = Boolean(pipelinePropertyId && pipelineId);
  const activeStrategyTab = strategyTabKey(strategy);

  return (
    <div className="grid min-h-0 grid-cols-1 gap-5 xl:grid-cols-2">
      {/* LEFT column — strategy bar (when in pipeline) + hero + deal inputs */}
      <div className="flex flex-col gap-4">
        {/* Pipeline Strategy Bar — mirrors the design's
            `.detail-left > .pipeline-strategy-bar`. Sits only over the left
            column so it doesn't overshoot the right (tabs) column. */}
        {isPipelined && onStrategyChange && (
          <div
            className={cn(
              "flex items-center gap-[14px] rounded-[10px] border border-primary px-[14px] py-[10px]",
              "bg-gradient-to-b from-primary/15 to-primary/5",
              "shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.12)]",
            )}
          >
            <span className="inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.02em] text-foreground">
              <Target className="h-4 w-4 text-primary" aria-hidden />
              Strategy
            </span>
            <Select
              value={strategy}
              onValueChange={(v) => onStrategyChange?.(v as Strategy)}
            >
              {/* Semantic tokens only — the trigger sits on a primary-tinted
                  bar in both themes, so it reads as a raised card surface
                  rather than a fixed dark panel. */}
              <SelectTrigger
                className={cn(
                  "h-[38px] flex-1 rounded-md border-border bg-card px-3 text-[13.5px] font-semibold text-foreground",
                  "hover:bg-muted/50",
                  "focus:border-primary",
                  "shadow-sm",
                )}
                aria-label="Pipeline strategy"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BRRRR">
                  BRRRR · Buy, Rehab, Rent, Refi, Repeat
                </SelectItem>
                <SelectItem value="Flip">Flip · Short-term resale</SelectItem>
                <SelectItem value="Rental">
                  Rental · Long-term buy &amp; hold
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <DetailHero snapshot={snapshot} purchase={state.purchase} />
        <DealInputs
          state={state}
          onChange={patch}
          strategy={strategy}
          onStrategyChange={setStrategy}
          hideStrategyBar={Boolean(isPipelined && onStrategyChange)}
          fromProperties={fromProperties}
          section8FmrEstimate={snapshot.section8Fmr ?? undefined}
          originalPurchase={initial.purchase}
          originalArv={initial.arv}
          onViewRehabBreakdown={() => setActiveTab("rehab")}
        />
      </div>

      {/* RIGHT column — tabs (sticky tab bar) */}
      <div className="flex flex-col self-start rounded-[10px] border border-border bg-card p-4">
        <Tabs value={tabValue} onValueChange={setActiveTab}>
          <TabsList
            className={cn(
              // Sticky header bar — bleeds into the panel's padding so when it
              // sticks it covers anything scrolling underneath.
              "sticky top-0 z-10 -mx-4 -mt-4 mb-4 h-auto w-full justify-start overflow-x-auto",
              "rounded-t-[10px] rounded-b-none border-b border-border bg-card px-4 pt-4",
            )}
          >
            {showChecklist && (
              <TabsTrigger value="checklist" className={tabTriggerStyle}>
                Checklist
              </TabsTrigger>
            )}
            {/* Strategy triggers — committed pipeline strategy hides the
                others; uncommitted (Find detail) shows all three with the
                inactive ones disabled + dimmed at 45% opacity. */}
            {(!hasCommittedStrategy || activeStrategyTab === "brrrr") && (
              <TabsTrigger
                value="brrrr"
                className={tabTriggerStyle}
                disabled={activeStrategyTab !== "brrrr"}
                title={
                  activeStrategyTab !== "brrrr"
                    ? "Switch the Financing strategy to BRRRR to enable this tab"
                    : undefined
                }
              >
                BRRRR
              </TabsTrigger>
            )}
            {(!hasCommittedStrategy || activeStrategyTab === "flip") && (
              <TabsTrigger
                value="flip"
                className={tabTriggerStyle}
                disabled={activeStrategyTab !== "flip"}
                title={
                  activeStrategyTab !== "flip"
                    ? "Switch the Financing strategy to Flip to enable this tab"
                    : undefined
                }
              >
                Flip
              </TabsTrigger>
            )}
            {(!hasCommittedStrategy || activeStrategyTab === "rent") && (
              <TabsTrigger
                value="rent"
                className={tabTriggerStyle}
                disabled={activeStrategyTab !== "rent"}
                title={
                  activeStrategyTab !== "rent"
                    ? "Switch the Financing strategy to Rental to enable this tab"
                    : undefined
                }
              >
                Rental
              </TabsTrigger>
            )}
            <TabsTrigger value="rehab" className={tabTriggerStyle}>
              Rehab
            </TabsTrigger>
            {/* Monthly Cash Flow isn't meaningful for a Flip — hide it when
                Flip is the active strategy (matches find-properties.html). */}
            {strategy !== "Flip" && (
              <TabsTrigger value="cashflow" className={tabTriggerStyle}>
                Monthly Cash Flow
              </TabsTrigger>
            )}
            <TabsTrigger value="overview" className={tabTriggerStyle}>
              Overview
            </TabsTrigger>
          </TabsList>

          {showChecklist && pipelineId && (
            <TabsContent value="checklist" className="mt-4">
              <ChecklistPanel
                pipelinePropertyId={pipelinePropertyId!}
                pipelineId={pipelineId}
                pipelineTitle={pipelineTitle ?? undefined}
              />
            </TabsContent>
          )}

          {/* Strategy panels — match the trigger visibility rule above.
              Radix `TabsContent` only mounts the active one, so calculator-
              heavy panels for inactive strategies don't render anyway. */}
          {(!hasCommittedStrategy || activeStrategyTab === "brrrr") && (
            <TabsContent value="brrrr" className="mt-4">
              <BrrrrSnapshot state={state} metrics={metrics} onChange={patch} />
            </TabsContent>
          )}
          {(!hasCommittedStrategy || activeStrategyTab === "flip") && (
            <TabsContent value="flip" className="mt-4">
              <FlipAnalysis state={state} metrics={metrics} onChange={patch} />
            </TabsContent>
          )}
          {(!hasCommittedStrategy || activeStrategyTab === "rent") && (
            <TabsContent value="rent" className="mt-4">
              <RentAnalysis
                state={state}
                metrics={metrics}
                sqft={sqft}
                bedrooms={snapshot.bd}
                onViewCashflowBreakdown={() => setActiveTab("cashflow")}
              />
            </TabsContent>
          )}
          {strategy !== "Flip" && (
            <TabsContent value="cashflow" className="mt-4">
              <MonthlyCashflow
                state={state}
                metrics={metrics}
                strategy={strategy}
                onCashflowChange={(p) =>
                  patch({ cashflowOverrides: { ...state.cashflowOverrides, ...p } })
                }
              />
            </TabsContent>
          )}
          <TabsContent value="rehab" className="mt-4">
            <RehabBreakdown
              lineItems={state.rehabLineItems ?? []}
              customItems={state.rehabCustomItems ?? []}
              onLineItemsChange={(items) => patch({ rehabLineItems: items, rehab: sumItems(items, state.rehabCustomItems ?? []) })}
              onCustomItemsChange={(items) => patch({ rehabCustomItems: items, rehab: sumItems(state.rehabLineItems ?? [], items) })}
            />
          </TabsContent>
          <TabsContent value="overview" className="mt-4">
            <OverviewPanel snapshot={snapshot} />
          </TabsContent>
        </Tabs>

        {isLoading && (
          <p className="mt-3 text-xs text-muted-foreground">
            Loading saved deal values…
          </p>
        )}
      </div>
    </div>
  );
});

function sumItems(
  a: Array<{ amount: number }>,
  b: Array<{ amount: number }>,
): number {
  return (
    a.reduce((s, i) => s + (i.amount || 0), 0) +
    b.reduce((s, i) => s + (i.amount || 0), 0)
  );
}
